import { useState } from "react";
import { callFunction } from "../lib/api";

// Connecting a QuickBooks Online company: Intuit's official "Connect to QuickBooks" button (public/brand/intuit, the
// graphics unmodified, at their own size; SOURCE.txt there says where they come from), the start of Intuit's sign-in,
// and the way back. Intuit sends the browser to the server, which sends it on to #/quickbooks?ref=… (or ?error=…);
// that screen keeps the answer for this tab and opens Settings → QuickBooks, where it is finished as the signed-in
// account (integrations { finish }). The code Intuit hands out never reaches the app.

const SIZES = { med: { w: 223, h: 36 }, short: { w: 163, h: 24 } } as const;

/** Intuit's Connect to QuickBooks button: default and hover graphics as Intuit supplies them. */
export function ConnectToQuickBooks({ onConnect, disabled, size = "med", title }: { onConnect: () => Promise<void> | void; disabled?: boolean; size?: keyof typeof SIZES; title?: string }) {
  const [busy, setBusy] = useState(false);
  const { w, h } = SIZES[size];
  const run = async () => {
    if (busy) return;
    setBusy(true);
    try { await onConnect(); } finally { setBusy(false); }
  };
  return (
    <button type="button" className={`c2qb c2qb-${size}`} aria-label="Connect to QuickBooks" title={title} disabled={disabled || busy} aria-busy={busy || undefined} onClick={() => void run()}>
      <img className="c2qb-default" src={`./brand/intuit/C2QB_transparent_btn_${size}_default.svg`} width={w} height={h} alt="Connect to QuickBooks" draggable={false} />
      <img className="c2qb-hover" src={`./brand/intuit/C2QB_transparent_btn_${size}_hover.svg`} width={w} height={h} alt="" aria-hidden="true" draggable={false} />
    </button>
  );
}

const WINDOW = "width=520,height=720";

/** Opens Intuit's sign-in at `url` in its window, from a click (the fallback link or a toast's button). */
export const openSignIn = (url: string) => window.open(url, "kept-qbo", WINDOW);

/**
 * Starts Intuit's sign-in (a reconnect names the connection, so it keeps its own environment). Call it straight from
 * the click: the window opens before the server is asked for the address (Safari and iPhone block a window opened
 * after a wait), then goes there. `blockedUrl`: the browser blocked the window; offer it as a link to click.
 * In Keept Runner for the desktop the sign-in opens in the system browser instead.
 */
export async function startQuickBooksSignIn(connectionId?: string): Promise<{ blockedUrl: string | null }> {
  const desktop = !!window.keptDesktop;
  const win = desktop ? null : window.open("", "kept-qbo", WINDOW);
  if (win) { try { win.document.title = "QuickBooks sign-in"; win.document.body.textContent = "Opening Intuit's sign-in…"; } catch { /* not ours to write */ } }
  let url: string;
  try {
    ({ url } = await callFunction<{ url: string }>("integrations", { action: "connect", provider: "quickbooks", ...(connectionId ? { connectionId } : {}) }));
  } catch (e) {
    win?.close();
    throw e;
  }
  if (desktop) { openSignIn(url); return { blockedUrl: null }; }
  if (!win || win.closed) return { blockedUrl: url };
  try { win.location.href = url; } catch { return { blockedUrl: url }; }
  return { blockedUrl: null };
}

// ---- The way back from Intuit ---------------------------------------------------------------------------------

const KEY = "kept.qbo.return";
export type QboReturn = { ref: string } | { error: string };

/** What #/quickbooks brought back, kept for this tab until Settings → QuickBooks takes it. */
export function keepReturn(params: URLSearchParams): void {
  const ref = params.get("ref") ?? "", error = params.get("error") ?? "";
  const value: QboReturn | null = /^[0-9a-f]{64}$/.test(ref) ? { ref } : error ? { error: error.slice(0, 20) } : null;
  try {
    if (value) sessionStorage.setItem(KEY, JSON.stringify({ ...value, at: Date.now() }));
  } catch { /* storage refused: the connections list still shows the outcome after a reload */ }
}

export function hasReturn(): boolean {
  try { return !!sessionStorage.getItem(KEY); } catch { return false; }
}

/** Takes (and forgets) what Intuit's redirect brought back; older than ten minutes is gone. */
export function takeReturn(): QboReturn | null {
  try {
    const raw = sessionStorage.getItem(KEY);
    sessionStorage.removeItem(KEY);
    if (!raw) return null;
    const v = JSON.parse(raw) as { ref?: string; error?: string; at?: number };
    if (!v.at || Date.now() - v.at > 10 * 60 * 1000) return null;
    return v.ref ? { ref: v.ref } : v.error ? { error: v.error } : null;
  } catch {
    return null;
  }
}

/** A connected company's line in Settings: Keept keeps every grant alive on its own, so she is asked to sign in again only
 *  when the grant is gone (revoked, or ended at Intuit) or Intuit's five-year limit is near (reauthorizeSoon). */
export function connectionState(c: { status: string; reauthorizeSoon?: boolean; reauthorizeBy?: string | null; statusNote?: string | null; refreshFailed?: boolean }): { label: string; tone: "green" | "amber"; reconnect: boolean } {
  if (c.status === "disconnected") return { label: "Disconnected in QuickBooks · sign in again", tone: "amber", reconnect: true };
  if (c.status !== "connected") return { label: "Sign-in needed · connect again", tone: "amber", reconnect: true };
  // Connected at Intuit, but not plainly "Connected": QuickBooks refuses the company, or takes no new data for it.
  if (c.statusNote === "company_refused") return { label: "QuickBooks refused access · check the client's subscription and your admin access, or Disconnect", tone: "amber", reconnect: true };
  if (c.statusNote === "company_inactive") return { label: "QuickBooks subscription ended — read-only in QuickBooks · Disconnect if the client left", tone: "amber", reconnect: false };
  if (c.refreshFailed) return { label: "Sign-in renewal failed · Keept tries again every hour", tone: "amber", reconnect: true };
  if (c.reauthorizeSoon && c.reauthorizeBy) return { label: `Connected · Intuit asks for a new sign-in by ${new Date(c.reauthorizeBy).toLocaleDateString()}`, tone: "amber", reconnect: true };
  return { label: "Connected · keeps itself signed in", tone: "green", reconnect: false };
}

/** Why the sign-in didn't connect, in words, from the server's short reason. */
export function returnMessage(error: string): string {
  switch (error) {
    case "cancelled": return "QuickBooks wasn't connected: the sign-in at Intuit was cancelled or refused. Who can connect a client: that client's admin, or the firm owner (primary admin) in Intuit Accountant Suite. Press Connect to QuickBooks to try again.";
    case "expired": return "That QuickBooks sign-in took too long or was already used. Press Connect to QuickBooks to start again.";
    case "invalid": return "That link isn't a QuickBooks sign-in Keept started. Press Connect to QuickBooks to start one.";
    default: return "QuickBooks wasn't connected because of a problem on Keept's side. Try again in a minute.";
  }
}

/** What came back from one sign-in, for "Connect all my clients": the company (and whether it was connected already), or why not. */
export interface SignInOutcome {
  company?: { connectionId: string; name: string; already: boolean }; error?: string;
  /** Why Keept didn't connect the company Intuit sent back, as the server said it (e.g. it is already connected to another
   *  Keept account): shown in the window the sign-in was started from, which the sign-in window may be hidden behind. */
  message?: string;
}
/** A server's reason as the opening window shows it: plain text, one line, capped. */
export const outcomeMessage = (v: unknown): string | undefined => (typeof v === "string" && v.trim() ? v.replace(/\s+/g, " ").trim().slice(0, 400) : undefined);

const CHANNEL = "kept-qbo";
/** This window, so it never answers its own announcement (Settings → QuickBooks listens in the sign-in window too). */
const WINDOW_ID = Math.random().toString(36).slice(2) + Date.now().toString(36);
type Announcement = { kept: "integration"; ok: boolean; id: string; from: string } & SignInOutcome;

/**
 * The sign-in window tells the window that opened it what came back. Through window.opener when the link is still there;
 * Intuit's pages may cut it (a Cross-Origin-Opener-Policy leaves the window without an opener after the round trip),
 * so then through a BroadcastChannel of this origin, which the opener answers (listenForSignIn). True: the opener heard
 * it (this window may close); false: nobody did, so this window keeps the outcome itself.
 */
export async function tellOpener(ok: boolean, outcome: SignInOutcome = {}, waitMs = 800): Promise<boolean> {
  try {
    if (window.opener && window.opener !== window) {
      (window.opener as Window).postMessage({ kept: "integration", ok, ...outcome }, location.origin);
      return true;
    }
  } catch { /* the opener went away */ }
  if (typeof BroadcastChannel === "undefined") return false;
  const id = Math.random().toString(36).slice(2);
  return await new Promise<boolean>((resolve) => {
    let ch: BroadcastChannel;
    try { ch = new BroadcastChannel(CHANNEL); } catch { resolve(false); return; }
    const end = (heard: boolean) => { clearTimeout(timer); try { ch.close(); } catch { /* closed */ } resolve(heard); };
    const timer = setTimeout(() => end(false), waitMs);
    ch.onmessage = (e: MessageEvent) => { const d = e.data as { kept?: string; id?: string }; if (d?.kept === "integration-heard" && d.id === id) end(true); };
    try { ch.postMessage({ kept: "integration", ok, ...outcome, id, from: WINDOW_ID } satisfies Announcement); } catch { end(false); }
  });
}

/**
 * The opening window's side of tellOpener's channel: each announcement from another window goes to `heard`, which says
 * whether this window is the one waiting for it (a loop is on, or a sign-in was started here); if so it is answered,
 * and the sign-in window closes. Returns the way to stop listening.
 */
export function listenForSignIn(heard: (d: { ok: boolean } & SignInOutcome) => boolean, self = WINDOW_ID): () => void {
  if (typeof BroadcastChannel === "undefined") return () => {};
  let ch: BroadcastChannel;
  try { ch = new BroadcastChannel(CHANNEL); } catch { return () => {}; }
  ch.onmessage = (e: MessageEvent) => {
    const d = e.data as Partial<Announcement>;
    if (d?.kept !== "integration" || typeof d.id !== "string" || d.from === self) return;
    const message = outcomeMessage(d.message);
    if (!heard({ ok: d.ok === true, ...(d.company ? { company: d.company } : {}), ...(d.error ? { error: d.error } : {}), ...(message ? { message } : {}) })) return;
    try { ch.postMessage({ kept: "integration-heard", id: d.id }); } catch { /* closed */ }
  };
  return () => { try { ch.close(); } catch { /* closed */ } };
}
